import { filters } from 'lib_ui-services';

const PROPERTIES_TO_SEARCH = ['assetNo', 'serialNo'];
/** Returned when several records match one candidate, so no single record can be chosen. */
const AMBIGUOUS = Symbol('ambiguous');

/**
 * The values to look for when marking, highest priority first.
 * That is the (possibly left padded) markText, followed by any fallback a use case supplied.
 * PALM adds the value the user actually entered, because not all PALM agencies use the full
 * 8 character tag length (see `FLAIR/willMark_item_item_PALM`).
 * @param {{markText?: string, alternateMarkTexts?: string[]}} [data]
 * @returns {string[]}
 */
export function getMarkTextCandidates(data) {
    const { markText, alternateMarkTexts = [] } = data ?? {};
    return [markText, ...alternateMarkTexts].filter(candidate => !!candidate);
}

/**
 * The value the user entered or scanned.
 * It is quoted in messages, because telling someone that `00000123` was not found when they
 * typed `123` is just confusing. It is also persisted: it is the `assetNo` of an asset created
 * from the mark screen (see `addNewItem` in `doingMark_item_item` and `doingMark_item_item_multi`).
 *
 * Position 0 of `alternateMarkTexts` is load bearing here: for matching it is only the highest
 * priority fallback, but this function also reads it as the value the user typed. Those are the
 * same string today because PALM is the only rule that sets the field and puts exactly one entry
 * in it. A use case that prepends a different candidate would have to pass the entered value
 * separately, or every message here would quote the wrong string, and an asset created from the
 * mark screen would be stored under the wrong number.
 * @param {{markText?: string, alternateMarkTexts?: string[]}} [data]
 * @returns {string|undefined}
 */
export function getEnteredMarkText(data) {
    const [enteredMarkText] = data?.alternateMarkTexts ?? [];
    return enteredMarkText ?? data?.markText;
}

/**
 * Build the filter that collects the records to consider.
 * Records in the grid use a fullText filter, so do the same here.
 * @param {object} [data]
 * @returns {object|undefined} undefined when there is nothing to search for
 */
export function getMarkTextQuery(data) {
    const { caseSensitive = false, exactMatchOnly = true } = data ?? {};
    const clauses = getMarkTextCandidates(data)
        .map(searchTerm =>
            filters.fullTextSearch.getMql({
                fullTextSearch: { searchTerm },
                propertiesToSearch: { propertiesToSearch: PROPERTIES_TO_SEARCH },
                caseSensitive,
                exactMatchOnly,
                preferLocal: true
            })
        )
        .filter(Boolean);

    if (!clauses.length) return undefined;
    if (clauses.length === 1) return clauses[0];
    //one flat $or over every candidate and property, rather than an $or per candidate
    return { $or: clauses.flatMap(clause => clause.$or ?? [clause]) };
}

/**
 * Narrow the records down to the single one that was marked.
 *
 * A candidate is resolved completely before the next candidate is tried. For one candidate:
 *   1. exact match, case sensitive, on assetNo, then on serialNo
 *   2. exact match, case insensitive, on assetNo only
 * We do NOT do partial matching. Whichever step results in a single record wins, independently
 * of the (in)active status of that match.
 *
 * Resolving one candidate at a time is what keeps the outcome the same as before a second
 * candidate existed: the markText was always matched by both steps, so a record that only
 * matches it case insensitively still wins over an exact match on the value the user entered.
 * The entered value is a fallback for when the padded value matches nothing at all.
 *
 * A candidate that matches several records ends the search: we cannot tell which record was
 * scanned, so the caller reports that instead of marking a record that matched a lower priority
 * candidate. Both steps still run for that candidate first, because a case sensitive match on
 * serialNo can be ambiguous while the case insensitive match on assetNo is not.
 * @param {object[]} itemRecords
 * @param {string[]} candidates from `getMarkTextCandidates`
 * @returns {object|undefined}
 */
export function findExactMatch(itemRecords, candidates) {
    for (const candidate of candidates) {
        const caseSensitive = findCaseSensitiveMatch(itemRecords, candidate);
        if (caseSensitive && caseSensitive !== AMBIGUOUS) return caseSensitive;

        const caseInsensitive = findCaseInsensitiveMatch(itemRecords, candidate);
        if (caseInsensitive && caseInsensitive !== AMBIGUOUS) return caseInsensitive;

        if (caseSensitive === AMBIGUOUS || caseInsensitive === AMBIGUOUS) return undefined;
    }
}

/**
 * The value is typed or scanned by the user, so it can contain characters that mean something
 * in a regular expression. Escape them exactly as `fullTextSearch.getMql` does, so the records
 * matched here are the same ones the query asked for, and a value like `a[b` cannot throw.
 */
function escapeForRegExp(candidate) {
    return candidate.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&');
}

function onlyMatch(matches) {
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) return AMBIGUOUS;
}

function findCaseSensitiveMatch(itemRecords, candidate) {
    const exactMatch = new RegExp(`^${escapeForRegExp(candidate)}$`);
    //prefer a match on assetNo over one on serialNo
    let matches = itemRecords.filter(item => item.assetNo?.match(exactMatch));
    if (!matches.length) {
        matches = itemRecords.filter(item => item.serialNo?.match(exactMatch));
    }
    return onlyMatch(matches);
}

/**
 * Deliberately looks at assetNo only. The serialNo fallback that belongs here has never run:
 * before this helper was extracted, the filter that read serialNo threw its own result away.
 * Repairing it would change marking for Asset Tracking and FLAIR as well, which is outside
 * the scope of https://github.com/sstid/backbone/issues/1388, so it is preserved as it was.
 * It does not have an issue of its own yet.
 */
function findCaseInsensitiveMatch(itemRecords, candidate) {
    const exactMatch = new RegExp(`^${escapeForRegExp(candidate)}$`, 'i');
    const matches = itemRecords.filter(item => item.assetNo?.match(exactMatch));
    return onlyMatch(matches);
}
